import React from "react";
import {
  ILanguageCourseWizardConfigs,
  IWizardConfig,
  WizardMode,
  SubmitButtonColor,
} from "../../../wizard/types";
import { createConfigStep1 } from "../create/step1/config";
import { createConfigStep2 } from "../create/step2/config";
import { createConfigStep3 } from "../create/step3/config";
import { createConfigStep4 } from "../create/step4/config";
import { createConfigStep5 } from "../create/step5/config";
import useLanguageCourseQueries from "../../hooks/useLanguageCourseQueries";
import { useSchoolId } from "../../../school/hooks/useSchoolId";
import { mergeCourseSteps } from "../../helpers/mergeCourseSteps";
import { parseToCourseBackEndModel } from "../../helpers/parseToCourseBackEndModel";
import { viewContentConfig } from "../view/viewContentConfig";
import requestCache from "../../../../utils/requestCache";
import useBranchQueries from "../../../school/hooks/useBranchQueries";
import { parseToViewCourse } from "../../helpers/parseToViewCourse";
import DeactivateContent from "../../../../components/DeactivateContent/DeactivateContent";
import { ENTITY_NAMES } from "../../../../constants";
import { parseToEditCourseInitialData } from "../../helpers/parseToEditCourseInitialData";
import { editConfigStep1 } from "../edit/step1/config";
import { editConfigStep2 } from "../edit/step2/config";
import { editConfigStep3 } from "../edit/step3/config";
import { editConfigStep4 } from "../edit/step4/config";
import deactivateFormValidator from "../validators/validators";
import DeleteContent from "../../components/DeleteContent";
import { getDeleteButtonConfigs } from "../../helpers/helperFunctions";

async function getCreateInitialData(schoolId) {
  return {
    schoolId,
  };
}

async function createCourse(formData, create) {
  const courseFrontEndModel = mergeCourseSteps(formData);
  const courseBackEndModel = parseToCourseBackEndModel(courseFrontEndModel);

  return await create(courseBackEndModel);
}

async function getViewData(fetchOne, fetchAllBranches, courseId) {
  const branchesPromise = requestCache<any>("branches", fetchAllBranches);
  const [data, branches] = await Promise.all([
    fetchOne(courseId),
    branchesPromise,
  ]);

  const previewData = parseToViewCourse({
    backEndData: data.data,
    branches: branches.data,
  });

  return {
    injectedData: {
      data: previewData,
    },
  };
}

async function getApplicationsCount(fetch, id) {
  const { data } = await fetch({ courseId: id });

  return {
    injectedData: {
      approved: data.approved,
      pending: data.pending,
    },
  };
}

async function getUpdateInitialData(fetchOne, courseId, schoolId) {
  const { data } = await fetchOne(courseId);

  return parseToEditCourseInitialData(data, schoolId);
}

async function updateCourse(formData, update) {
  const courseFrontEndModel = mergeCourseSteps(formData);
  const courseBackEndModel = parseToCourseBackEndModel(courseFrontEndModel);

  return await update(courseBackEndModel);
}

export function useLanguageCourseConfig(): ILanguageCourseWizardConfigs {
  const {
    create,
    update,
    fetchOne,
    checkDuplication,
    activate,
    deactivate,
    fetchAppCount,
    deleteOne,
  } = useLanguageCourseQueries();
  const schoolId = useSchoolId();
  const { fetchAll: fetchAllBranches } = useBranchQueries();

  const fetchAllBranchesModified = () => fetchAllBranches({ schoolId });

  const getCreateConfig = (): IWizardConfig => ({
    steps: [
      { ...createConfigStep1, backendValidator: checkDuplication },
      createConfigStep2,
      createConfigStep3,
      createConfigStep4,
      createConfigStep5,
    ],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Create,
    getInitialData: () => getCreateInitialData(schoolId),
    saveCallback: (data) => createCourse(data, create),
  });

  const getEditConfig = (id): IWizardConfig => ({
    steps: [
      { ...editConfigStep1, backendValidator: checkDuplication },
      editConfigStep2,
      editConfigStep3,
      editConfigStep4,
    ],
    submitButtonText: "Save",
    submitButtonColor: SubmitButtonColor.Success,
    mode: WizardMode.Edit,
    getInitialData: () => getUpdateInitialData(fetchOne, id, schoolId),
    saveCallback: (data) => updateCourse(data, update),
  });

  const getViewConfig = (id): IWizardConfig => ({
    steps: [viewContentConfig],
    submitButtonText: null,
    cancelButtonText: "Close",
    mode: WizardMode.View,
    getInitialData: () => getViewData(fetchOne, fetchAllBranchesModified, id),
  });

  const getActivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Activate course",
        },
        content: `Are you sure you want to activate this course?`,
      },
    ],
    submitButtonText: "Activate",
    cancelButtonText: "Close",
    saveCallback: () => activate(id),
  });

  const getDeactivateConfig = (id): IWizardConfig => ({
    steps: [
      {
        titleConfig: {
          titleText: "Deactivate course",
        },
        width: "552px",
        content: () => <DeactivateContent name={ENTITY_NAMES.course} />,
        validator: deactivateFormValidator,
      },
    ],
    submitButtonText: "Deactivate",
    submitButtonColor: SubmitButtonColor.Danger,
    cancelButtonText: "Close",
    saveCallback: (data) =>
      deactivate(id, {
        text: data?.reason ?? "",
      }),
    getInitialData: () => getApplicationsCount(fetchAppCount, id),
  });

  const getDeleteConfig = (id): IWizardConfig => ({
    steps: [
      {
        width: "430px",
        titleConfig: {
          titleText: "Delete course",
        },
        content: () => <DeleteContent name={ENTITY_NAMES.course} />,
      },
    ],
    mode: WizardMode.Delete,
    getInitialData: () => getApplicationsCount(fetchAppCount, id),
    saveCallback: () => deleteOne(id),
    getButtonConfigs: getDeleteButtonConfigs,
  });

  return {
    getCreateConfig,
    getEditConfig,
    getViewConfig,
    getActivateConfig,
    getDeactivateConfig,
    getDeleteConfig,
  };
}
